import { FocusEvent } from "react";

import { Nullable, StaticVariableTag } from "@simplyk/common";
import { FroalaEditor, FroalaOptions } from "froala-editor";
import { RegisterOptions, FieldError, Path, FieldValues } from "react-hook-form";

import { AmplitudeEvents } from "@/common/constants/amplitude";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { EmailDeliveryOutput } from "@/features/Campaigns/tabs/CampaignCommunicationTab/useCampaignCommunication";

export enum FroalaConfig {
  Default = "Default",
  Email = "Email",
  Minimalist = "Minimalist",
  PostMail = "PostMail",
  ExtraThankYouEmail = "ExtraThankYouEmail",
}

export type FroalaInitControls = {
  initialize: () => void;
  destroy: () => void;
  getEditor: () => FroalaEditor;
};

export type CustomFroalaConfig = {
  date?: boolean;
  location?: boolean;
  eTicket?: boolean;
  viewTickets?: boolean;
};

export type StaticVariableLabels = Partial<Record<StaticVariableTag, string>>;

export type ExtraWysiwygVariable = {
  tag: string;
  label: string;
  tooltip?: string;
  dividerBefore?: boolean;
};

export type ActivePrefilledFormVar = {
  key: string;
  element: Element | null;
  initialization?: boolean;
};

export interface OutlinedTextFieldWysiwygProps<T extends FieldValues> extends Pick<
  FroalaOptions,
  "height" | "heightMax" | "placeholderText" | "zIndex"
> {
  id: string;
  name: Path<T>;
  defaultValue: string | undefined;
  attachmentKeyName?: Path<T>;
  error?: FieldError;
  onChange?: (value: string) => void;
  footer?: React.ReactElement<unknown>;
  rules?: RegisterOptions[];
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  keyName?: string;
  config?: FroalaConfig;
  customConfig?: CustomFroalaConfig;
  extraVariables?: ExtraWysiwygVariable[];
  staticVariableLabels?: StaticVariableLabels;
  onFocus?: () => void;
  onEditorFocus?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  addVariableAmplitudeEvent?: AmplitudeEvents;
  subjectName?: Path<T>;
  subjectRef?: React.MutableRefObject<Nullable<HTMLInputElement>>;
  "data-test"?: string;
  prefillWithTextButton?: string;
  heightMinOver310: number; // enforce min height to 310 to be sure that the add link modal will be displayed correctly
  emailDelivery?: Pick<EmailDeliveryOutput, "id" | "locale" | "subject" | "recipientEmails" | "emailAttachmentUrl">;
  style?: React.CSSProperties;
  disabled?: boolean;
  compact?: boolean;
  compactEmailActions?: "menu" | "toolbar";
  simplifiedMobileToolbar?: boolean;
}
